import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Scales, FileText, LockKey, CalendarCheck } from "@phosphor-icons/react";

const TABS = ["impressum", "datenschutz", "agb"];

const MONTHS_DE = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
];

function formatStandDate(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    if (lang === "en") {
        return d.toLocaleDateString("en-GB", {
            day: "numeric", month: "long", year: "numeric",
        });
    }
    return `${d.getDate()}. ${MONTHS_DE[d.getMonth()]} ${d.getFullYear()}`;
}

// Split first H1 from body so the "Stand" badge can be injected between them.
function splitH1(content) {
    if (!content) return { h1: null, rest: "" };
    const lines = content.split(/\r?\n/);
    let h1 = null;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
        if (/^# /.test(lines[i])) {
            h1 = lines[i].slice(2);
            idx = i;
            break;
        }
    }
    if (idx < 0) return { h1: null, rest: content };
    const rest = lines.slice(idx + 1).join("\n");
    return { h1, rest };
}

function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split(/\r?\n/);
    const out = [];
    let paragraph = [];
    const flushPara = () => {
        if (paragraph.length) {
            out.push(
                <p key={`p-${out.length}`} className="text-shield-ink leading-relaxed my-3">
                    {paragraph.map((part, i) => (
                        <span key={i}>
                            {part}
                            {i < paragraph.length - 1 ? " " : ""}
                        </span>
                    ))}
                </p>,
            );
            paragraph = [];
        }
    };
    const renderInline = (line) => {
        // split by **bold** segments preserving order
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) =>
            /^\*\*.+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
        );
    };
    for (const line of lines) {
        if (/^# /.test(line)) {
            flushPara();
            out.push(<h1 key={out.length} className="font-heading font-bold text-3xl text-shield-ink mt-8 mb-4">{line.slice(2)}</h1>);
        } else if (/^## /.test(line)) {
            flushPara();
            out.push(<h2 key={out.length} className="font-heading font-semibold text-xl text-shield-ink mt-6 mb-2">{line.slice(3)}</h2>);
        } else if (/^- /.test(line)) {
            flushPara();
            out.push(
                <li key={out.length} className="ml-6 list-disc text-shield-ink">
                    {renderInline(line.slice(2))}
                </li>,
            );
        } else if (line.trim() === "") {
            flushPara();
        } else {
            paragraph.push(<>{renderInline(line)}</>);
        }
    }
    flushPara();
    return out;
}

export default function Legal() {
    const { t, lang } = useApp();
    const [sp, setSp] = useSearchParams();
    const initial = TABS.includes(sp.get("tab")) ? sp.get("tab") : "impressum";
    const [tab, setTab] = useState(initial);
    const [docs, setDocs] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/legal");
                setDocs(r.data);
            } catch {
                /* fetch failed — user sees loading placeholder */
            }
        })();
    }, []);

    const change = (k) => {
        setTab(k);
        setSp({ tab: k });
    };

    const tabIcons = { impressum: Scales, datenschutz: LockKey, agb: FileText };
    const doc = docs?.[tab];

    return (
        <AppShell>
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10 lg:py-14">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                        {t("legal.title")}
                    </div>
                    <h1 className="mt-2 font-heading font-bold text-shield-ink text-3xl sm:text-4xl" data-testid="legal-title">
                        {t("legal.title")}
                    </h1>
                </div>

                <div className="mt-8 flex gap-2 flex-wrap" role="tablist">
                    {TABS.map((k) => {
                        const Icon = tabIcons[k];
                        const active = tab === k;
                        return (
                            <button
                                key={k}
                                onClick={() => change(k)}
                                role="tab"
                                aria-selected={active}
                                className={`inline-flex items-center gap-2 h-11 px-5 pill font-semibold transition-colors ${active ? "bg-shield-forest text-white" : "bg-white border border-shield-line text-shield-ink hover:bg-shield-sage"}`}
                                data-testid={`legal-tab-${k}`}
                            >
                                <Icon size={16} weight="bold" aria-hidden="true" />
                                {t(`legal.tabs.${k}`)}
                            </button>
                        );
                    })}
                </div>

                <article className="mt-8 card-soft p-8 lg:p-10" data-testid={`legal-content-${tab}`}>
                    {docs ? (
                        (() => {
                            const { h1, rest } = splitH1(doc?.content);
                            const standStr = formatStandDate(doc?.updated_at, lang);
                            return (
                                <>
                                    {h1 && (
                                        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-shield-ink mb-4" data-testid={`legal-h1-${tab}`}>
                                            {h1}
                                        </h1>
                                    )}
                                    {standStr && (
                                        <div className="mb-8" data-testid={`legal-stand-${tab}`}>
                                            <span className="inline-flex items-center gap-2 h-8 px-3 pill bg-shield-sage/60 border border-shield-line text-xs font-semibold uppercase tracking-[0.15em] text-shield-forest">
                                                <CalendarCheck size={14} weight="fill" aria-hidden="true" />
                                                {t("legal.asOf")}: <span className="font-heading normal-case tracking-normal text-shield-ink">{standStr}</span>
                                            </span>
                                            <div className="mt-4 h-px w-16 bg-shield-terracotta rounded-full" aria-hidden="true" />
                                        </div>
                                    )}
                                    <div>{renderMarkdown(rest)}</div>
                                </>
                            );
                        })()
                    ) : (
                        <p className="text-shield-muted">{t("common.loading")}</p>
                    )}
                </article>

                <div className="mt-8">
                    <Link to="/" className="text-sm text-shield-forest hover:underline">← {t("common.back")}</Link>
                </div>
            </div>
        </AppShell>
    );
}
